import React, { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"

type State = {
  unSavedMedicationIds: string[]
  removedMedicationIds: string[]
}

type Actions = {
  setUnSavedMedicationIds: (medicationId: string) => void
  setRemovedMedicationIds: (medicationId: string) => void
  removeUnSavedMedicationId: (medicationId: string) => void
  reset: () => void
}

const initialState: State = {
  unSavedMedicationIds: [],
  removedMedicationIds: [],
}

const createDePrescriptionStore = () => {
  return create<State & Actions>((set, get) => ({
    ...initialState,
    setUnSavedMedicationIds: (medicationId) =>
      set({ unSavedMedicationIds: [...get().unSavedMedicationIds, medicationId] }),
    setRemovedMedicationIds: (medicationId) =>
      set({ removedMedicationIds: [...get().removedMedicationIds, medicationId] }),
    removeUnSavedMedicationId: (medicationId) => {
      const unSavedMedicationIds = get().unSavedMedicationIds.filter((id) => id !== medicationId)
      set({ unSavedMedicationIds })
    },
    reset: () => set(initialState),
  }))
}

type DePrescriptionStoreType = ReturnType<typeof createDePrescriptionStore>

const DePrescriptionStoreContext = createContext<DePrescriptionStoreType | null>(null)

type DePrescriptionStoreProviderProps = React.PropsWithChildren

export const DePrescriptionStoreProvider = ({ children }: DePrescriptionStoreProviderProps) => {
  const storeRef = useRef<DePrescriptionStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createDePrescriptionStore()
  }
  return <DePrescriptionStoreContext.Provider value={storeRef.current}>{children}</DePrescriptionStoreContext.Provider>
}

export function useDePrescriptionStoreContext<T>(selector: (state: State & Actions) => T): T {
  const store = useContext(DePrescriptionStoreContext)
  if (!store) throw new Error("Missing PrescriptionStoreProvider in the tree")
  return useStore(store, selector)
}
